import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faBoxesStacked,
  faCalculator,
  faCashRegister,
  faCheckCircle,
  faCircleNodes,
  faClipboardCheck,
  faFileInvoiceDollar,
  faQuestionCircle,
  faShieldHalved,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import "./BillingSoftware.css";
import "./Comparisons.css";

const path = "/comparisons";
const title = "Business Software Comparison & Buying Guide | Auditus-iERP";
const description =
  "Compare ERP, billing, accounting, inventory, POS, GST, and integrated business software. Learn what to check before choosing software for your business.";

const softwareTypes = [
  {
    icon: faCircleNodes,
    title: "ERP Software",
    route: "/erp-software",
    purpose: "Connects multiple business functions in one system.",
    users: "Owners, managers, billing teams, purchase teams, stock teams, and accountants.",
    features: "Billing, accounting, inventory, POS, GST, sales, purchases, customers, suppliers, payments, and reports.",
    need: "Useful when a business wants connected operations instead of separate tools.",
  },
  {
    icon: faFileInvoiceDollar,
    title: "Billing Software",
    route: "/billing-software",
    purpose: "Creates invoices and organizes sales or billing transactions.",
    users: "Billing counters, sales teams, retail stores, wholesalers, and small businesses.",
    features: "Invoice generation, GST billing, product selection, customers, payments, and sales records.",
    need: "Useful when invoicing speed and billing accuracy are the immediate priority.",
  },
  {
    icon: faCalculator,
    title: "Accounting Software",
    route: "/accounting-software",
    purpose: "Manages financial records and reporting.",
    users: "Owners, accountants, finance teams, and businesses tracking transactions.",
    features: "Ledgers, vouchers, expenses, payments, receivables, payables, GST records, and financial reports.",
    need: "Useful when financial organization, account history, and reports matter most.",
  },
  {
    icon: faBoxesStacked,
    title: "Inventory Management Software",
    route: "/inventory-management-software",
    purpose: "Organizes product and stock activity.",
    users: "Stock teams, retailers, supermarkets, distributors, and purchase teams.",
    features: "Products, stock tracking, purchases, sales, barcode movement, warehouses, batches, reorder levels, and item reports.",
    need: "Useful when stock visibility and product movement are central to the business.",
  },
  {
    icon: faCashRegister,
    title: "POS Software",
    route: "/pos-software",
    purpose: "Supports point-of-sale checkout workflows.",
    users: "Retail stores, supermarkets, grocery counters, textile stores, and checkout teams.",
    features: "POS billing, barcode product selection, payments, customers, inventory connection, and sales reports.",
    need: "Useful when fast counter billing and retail checkout are daily requirements.",
  },
];

const erpVsBillingRows = [
  ["Main purpose", "Connect many business functions.", "Create and manage invoices or billing records."],
  ["Billing", "Often included as part of operations.", "Primary focus."],
  ["Accounting", "Can connect with financial records.", "Usually limited unless integrated."],
  ["Inventory", "Can connect stock with purchases and sales.", "May connect to stock depending on the software."],
  ["POS", "Can include POS for retail workflows.", "May include POS or remain invoice-focused."],
  ["GST", "Can connect GST with billing, accounting, and reports.", "Often supports GST-ready invoices."],
  ["Sales", "Can connect sales with customers, stock, payments, and reports.", "Usually records billing-related sales."],
  ["Purchases", "Can connect purchases with suppliers, stock, and accounts.", "May support purchase billing if included."],
  ["Reporting", "Broader business reporting.", "Billing and sales-focused reporting."],
  ["Business management", "Wider operational management.", "Focused on billing work."],
  ["Integration", "Built around connected modules.", "Depends on the billing product."],
];

const inventoryVsErpRows = [
  ["Products", "Primary focus.", "Included with other business records."],
  ["Stock", "Primary focus.", "Connected to sales, purchases, billing, and reports."],
  ["Purchases", "Often connected to incoming stock.", "Can connect suppliers, purchases, stock, and accounts."],
  ["Sales", "Often connected to outgoing stock.", "Can connect sales, billing, customers, payments, and stock."],
  ["Billing", "May be outside the inventory tool.", "Can be part of the connected system."],
  ["Accounting", "Usually separate unless integrated.", "Can connect stock activity with financial records."],
  ["GST", "Usually not the main focus.", "Can connect GST workflows with transactions and reports."],
  ["Reporting", "Stock and item reports.", "Business-wide reports across modules."],
  ["Business management", "Stock-focused.", "Operations-wide."],
];

const separateVsIntegrated = [
  ["Data entry", "Teams may enter similar information in multiple places.", "Shared records can reduce repeated entry."],
  ["Information flow", "Information may move manually between tools.", "Modules can work from connected transaction data."],
  ["Billing", "Billing can be handled well as a focused task.", "Billing can connect with stock, accounts, GST, customers, and reports."],
  ["Accounting", "Accounts may need imports or manual matching.", "Accounting can connect with sales, purchases, payments, and expenses."],
  ["Inventory", "Stock may need separate updates.", "Inventory can connect with purchases, sales, billing, barcode movement, and reports."],
  ["Reporting", "Reports may need data from more than one system.", "Reports can use the connected business records."],
  ["Business visibility", "Useful for separate teams, but harder to centralize.", "Owners can review connected business activity more easily."],
  ["Maintenance", "Each tool may need separate setup and support.", "One platform can simplify management when the modules fit."],
  ["Workflow connection", "Depends on exports, imports, or integrations.", "Connection is part of the platform design."],
];

const featureMatrix = [
  ["Billing", "Often", "Primary", "Connected", "Sometimes", "Primary"],
  ["Invoice generation", "Often", "Primary", "Sometimes", "Sometimes", "Primary"],
  ["Accounting", "Often", "Limited", "Primary", "Limited", "Limited"],
  ["Ledger management", "Often", "Limited", "Primary", "Limited", "Limited"],
  ["Inventory", "Often", "Sometimes", "Sometimes", "Primary", "Often"],
  ["Stock tracking", "Often", "Sometimes", "Limited", "Primary", "Often"],
  ["POS", "Sometimes", "Sometimes", "Limited", "Limited", "Primary"],
  ["GST", "Often", "Often", "Often", "Sometimes", "Often"],
  ["Sales", "Often", "Often", "Sometimes", "Often", "Primary"],
  ["Purchases", "Often", "Sometimes", "Often", "Often", "Limited"],
  ["Customers", "Often", "Often", "Sometimes", "Sometimes", "Often"],
  ["Suppliers", "Often", "Sometimes", "Often", "Often", "Limited"],
  ["Payments", "Often", "Often", "Primary", "Sometimes", "Often"],
  ["Reports", "Broad", "Billing reports", "Financial reports", "Stock reports", "Sales reports"],
  ["Business management", "Broad", "Billing-focused", "Finance-focused", "Stock-focused", "Retail-focused"],
];

const decisionGuide = [
  ["Need only basic invoicing?", "Billing Software", "/billing-software"],
  ["Need financial record management?", "Accounting Software", "/accounting-software"],
  ["Need stock control?", "Inventory Management Software", "/inventory-management-software"],
  ["Need retail checkout?", "POS Software", "/pos-software"],
  ["Need multiple connected business functions?", "ERP Software", "/erp-software"],
];

const checklist = [
  "Business type",
  "Number of users",
  "Billing requirements",
  "Accounting requirements",
  "Inventory requirements",
  "POS requirements",
  "GST requirements",
  "Reporting requirements",
  "Integration requirements",
  "Ease of use",
  "Scalability",
  "Support",
  "Pricing/value",
  "Data security requirements",
];

const buyingQuestions = [
  "Does it support my business type?",
  "Does it support billing?",
  "Does it support accounting?",
  "Does it support inventory?",
  "Does it support GST workflows?",
  "Does it support POS?",
  "Can modules work together?",
  "Can I see a live demo?",
  "What reports are available?",
  "What support is available?",
  "What is the pricing?",
  "Can the software grow with my business?",
];

const featureResearch = [
  ["Billing Features", "Billing features cover invoice creation, customer details, product selection, tax details, payments, and billing reports.", "Buyers should check invoice formats, GST-ready billing, stock connection, customer records, and payment tracking.", "Auditus-iERP supports GST-ready invoices, product selection, customers, payments, barcode workflows, inventory connection, and sales reports."],
  ["Accounting Features", "Accounting features organize the financial record behind daily transactions.", "Buyers should check ledgers, vouchers, expenses, receivables, payables, payments, GST records, and reporting.", "Auditus-iERP supports ledgers, vouchers, sales, purchases, expenses, payments, GST-related records, and financial reports."],
  ["Inventory Features", "Inventory features help teams track product and stock movement.", "Buyers should check products, stock tracking, purchase and sales connection, barcode workflows, warehouses, batches, reorder levels, and reports.", "Auditus-iERP supports product and stock management, barcode movement, warehouses, batches, reorder levels, purchases, sales, and item reports."],
  ["POS Features", "POS features support retail checkout and counter billing.", "Buyers should check checkout speed, billing, product search, barcode support, payments, customers, stock connection, and sales reports.", "Auditus-iERP supports POS billing, barcode product selection, customers, payment recording, inventory connection, and reports."],
  ["GST Features", "GST features help keep tax information close to transactions and reports.", "Buyers should check GST-ready invoices, tax calculations, GSTIN, HSN/SAC, reports, and billing/accounting connection.", "Auditus-iERP supports GST-ready invoices, tax calculation workflows, GSTIN, HSN/SAC, e-invoice workflows, e-way bill workflows, and GST reports."],
  ["Reporting Features", "Reporting features turn daily records into business visibility.", "Buyers should check sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports.", "Auditus-iERP includes sales, purchase, inventory, GST, financial, audit, item-wise, and dashboard reports."],
];

const industryComparisons = [
  ["Supermarket", "POS + Billing + Inventory + GST + Reports"],
  ["Retail", "POS + Billing + Inventory"],
  ["Textile", "Billing + Inventory + Accounting"],
  ["Wholesale", "Billing + Inventory + Accounting"],
  ["Distributor", "Billing + Inventory + Accounting"],
];

const auditusModules = [
  ["ERP", "/erp-software"],
  ["Billing", "/billing-software"],
  ["Accounting", "/accounting-software"],
  ["Inventory", "/inventory-management-software"],
  ["POS", "/pos-software"],
  ["GST", "/gst-software"],
  ["Sales", "/features-solutions"],
  ["Purchases", "/features-solutions"],
  ["Customers", "/features-solutions"],
  ["Suppliers", "/features-solutions"],
  ["Reports", "/features-solutions"],
];

const screenshots = [
  ["/images/screenshots/dashboard.png", "Auditus-iERP ERP dashboard for business software comparison", "ERP dashboard"],
  ["/images/screenshots/invoice.png", "Auditus-iERP billing and POS software screen", "Billing and POS"],
  ["/images/screenshots/reports.png", "Auditus-iERP accounting and business reports screen", "Reports"],
  ["/images/gstdashboard.PNG", "Auditus-iERP GST software dashboard", "GST dashboard"],
];

const faqs = [
  ["What is the difference between ERP software and billing software?", "ERP software connects multiple business functions such as billing, accounting, inventory, POS, GST, purchases, sales, customers, suppliers, and reports. Billing software primarily focuses on invoices and billing transactions."],
  ["What is the difference between billing software and accounting software?", "Billing software focuses on invoices, sales, customers, products, and payments. Accounting software focuses on financial records, ledgers, vouchers, expenses, payments, and reports."],
  ["What is the difference between POS software and billing software?", "POS software is designed for point-of-sale checkout workflows. Billing software is broader invoicing software. They overlap where retail billing, products, customers, payments, and reports are involved."],
  ["What is the difference between inventory software and ERP software?", "Inventory software focuses on products and stock. ERP software can include inventory while also connecting billing, accounting, purchases, sales, GST, customers, suppliers, and reports."],
  ["Should a small business use ERP software?", "A small business should consider ERP software when it needs multiple connected functions such as billing, accounting, inventory, GST, payments, customers, suppliers, and reports."],
  ["What features should I look for in billing software?", "Look for invoice generation, GST-ready billing, product selection, customer records, payment recording, inventory connection, and useful sales reports."],
  ["What features should I look for in accounting software?", "Look for ledgers, vouchers, transactions, expenses, payments, receivables, payables, GST-related records, and financial reports."],
  ["What features should I look for in inventory management software?", "Look for product records, stock tracking, purchase and sales connection, barcode movement, warehouses, batches, reorder levels, and item-wise reports."],
  ["What features should I look for in POS software?", "Look for fast checkout, billing, product selection, barcode support, payments, customers, inventory connection, and sales reports."],
  ["What should I check before choosing business software?", "Check your business type, user count, billing, accounting, inventory, POS, GST, reporting, integration, ease of use, support, pricing, and data security needs."],
  ["Is integrated software better than separate software?", "It depends on your workflow. Separate software can work for focused tasks, while integrated software is useful when your billing, accounts, stock, GST, payments, and reports need to work together."],
  ["Can billing, accounting and inventory work together?", "Yes. Auditus-iERP connects billing, accounting, and inventory through invoices, sales, purchases, products, stock, payments, ledgers, GST workflows, and reports."],
  ["How can I compare Auditus-iERP with other software?", "Compare the modules, supported business workflows, screenshots, reports, pricing, support, ease of use, and whether the system fits your daily operations."],
  ["Can I get a demo of Auditus-iERP?", "Yes. Use the Book a Demo button to open the demo request form and share your business requirements."],
];

function setMeta(name, content, attribute = "name") {
  let tag = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function ComparisonTable({ rows, columns, label }) {
  return (
    <div className="cmp-table" role="table" aria-label={label}>
      <div className="cmp-table-row cmp-table-head" role="row">
        {columns.map((column) => (
          <span role="columnheader" key={column}>{column}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div className="cmp-table-row" role="row" key={row[0]}>
          {row.map((cell, index) =>
            index === 0 ? <strong role="cell" key={cell}>{cell}</strong> : <p role="cell" key={cell}>{cell}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Comparisons() {
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    setMeta("description", description);
    setMeta("robots", "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", path, "property");
    setMeta("og:image", "/images/screenshots/dashboard.png", "property");
    setLink("canonical", path);

    const schema = document.createElement("script");
    schema.id = "comparisons-schema";
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "/" },
            { "@type": "ListItem", position: 2, name: "Software Comparisons", item: path },
          ],
        },
        {
          "@type": "SoftwareApplication",
          name: "Auditus-iERP",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Windows, Android",
          url: path,
          image: "/images/screenshots/dashboard.png",
          description,
          featureList: auditusModules.map(([name]) => name),
        },
        {
          "@type": "FAQPage",
          mainEntity: faqs.map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
      ],
    });
    document.head.appendChild(schema);

    return () => {
      document.title = previousTitle;
      schema.remove();
    };
  }, []);

  return (
    <main className="billing-page cmp-page">
      <section className="cmp-hero" aria-labelledby="comparisons-title">
        <div className="billing-container cmp-hero-grid">
          <div>
            <nav className="billing-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Software Comparisons</span>
            </nav>
            <p className="billing-kicker">
              <FontAwesomeIcon icon={faClipboardCheck} aria-hidden="true" />
              Business software comparison
            </p>
            <h1 id="comparisons-title">Compare Business Software and Find the Right Solution</h1>
            <p className="billing-lead">
              Use this guide to understand the differences between ERP, billing,
              accounting, inventory management, and POS software before choosing the
              right setup for your business.
            </p>
            <div className="billing-actions">
              <Link to="/features-solutions" className="billing-btn billing-btn-primary">
                Explore Auditus-iERP <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Demo" className="billing-btn billing-btn-secondary">
                Book a Demo
              </Link>
            </div>
          </div>
          <div className="billing-window">
            <div className="billing-window-bar">
              <span>Auditus-iERP - Comparison Guide</span>
              <i />
            </div>
            <img
              src="/images/screenshots/dashboard.png"
              alt="Auditus-iERP ERP dashboard for business software comparison"
              width="920"
              height="560"
              loading="eager"
            />
            <div className="billing-window-foot">
              <span>ERP</span>
              <span>Billing</span>
              <span>Inventory</span>
              <span>POS</span>
            </div>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="types-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Software types</p>
            <h2 id="types-title">Understanding Different Types of Business Software</h2>
            <p>
              Each software category has a different purpose. The right choice depends
              on whether your business needs one focused tool or connected workflows.
            </p>
          </div>
          <div className="cmp-type-grid">
            {softwareTypes.map((item) => (
              <article key={item.title}>
                <div className="billing-feature-icon">
                  <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
                </div>
                <h3>{item.title}</h3>
                <p><strong>Purpose:</strong> {item.purpose}</p>
                <p><strong>Typical users:</strong> {item.users}</p>
                <p><strong>Main features:</strong> {item.features}</p>
                <p><strong>When needed:</strong> {item.need}</p>
                <Link to={item.route}>Learn more <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" /></Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section cmp-soft" aria-labelledby="erp-billing-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Category comparison</p>
            <h2 id="erp-billing-title">ERP Software vs Billing Software</h2>
            <p>
              ERP software usually covers a wider business management system, while
              billing software focuses on invoices and billing workflows. Auditus-iERP
              fits as an integrated platform with verified ERP, billing, accounting,
              inventory, POS, GST, sales, purchases, customer, supplier, and report modules.
            </p>
          </div>
          <ComparisonTable rows={erpVsBillingRows} columns={["Category", "ERP Software", "Billing Software"]} label="ERP software compared with billing software" />
        </div>
      </section>

      <section className="billing-section" aria-labelledby="billing-accounting-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Billing vs accounting</p>
            <h2 id="billing-accounting-title">Billing Software vs Accounting Software</h2>
          </div>
          <div className="billing-copy">
            <p>
              Billing software focuses primarily on sales, invoices, billing workflows,
              customers, products, payments, and sales records. Accounting software
              focuses on financial records, transactions, ledgers, vouchers, expenses,
              payments, and financial reporting.
            </p>
            <p>
              Integrated software can connect both areas so billing transactions support
              accounting records. Compare the <Link to="/billing-software">billing software</Link>
              {" "}and <Link to="/accounting-software">accounting software</Link> pages for details.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section cmp-dark" aria-labelledby="pos-billing-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Retail comparison</p>
            <h2 id="pos-billing-title">POS Software vs Billing Software</h2>
          </div>
          <div className="billing-copy">
            <p>
              POS software is focused on point-of-sale and checkout workflows. Billing
              software is focused primarily on invoicing and billing records. They
              overlap when retail billing needs products, customers, payments, barcode
              workflows, stock connection, and reports.
            </p>
            <p>
              Auditus-iERP supports both POS and billing workflows. Explore
              {" "}<Link to="/pos-software">POS software</Link> and
              {" "}<Link to="/billing-software">billing software</Link>.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="inventory-erp-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Stock comparison</p>
            <h2 id="inventory-erp-title">Inventory Management Software vs ERP Software</h2>
            <p>
              Inventory management software focuses on product and stock control. ERP
              software can include inventory and connect it with sales, purchases,
              billing, accounting, GST, and reporting.
            </p>
          </div>
          <ComparisonTable rows={inventoryVsErpRows} columns={["Category", "Inventory Software", "ERP Software"]} label="Inventory software compared with ERP software" />
          <div className="cmp-inline-links">
            <Link to="/inventory-management-software">Inventory management software</Link>
            <Link to="/erp-software">ERP software</Link>
          </div>
        </div>
      </section>

      <section className="billing-section cmp-soft" aria-labelledby="separate-integrated-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">System choice</p>
            <h2 id="separate-integrated-title">Separate Business Software vs Integrated ERP</h2>
            <p>
              Separate software can be the right choice for focused needs. Integrated
              ERP becomes useful when several teams need the same business data to work
              across billing, accounts, stock, GST, payments, and reports.
            </p>
          </div>
          <ComparisonTable rows={separateVsIntegrated} columns={["Category", "Separate Systems", "Integrated ERP"]} label="Separate business software compared with integrated ERP" />
        </div>
      </section>

      <section className="billing-section" aria-labelledby="feature-table-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Feature matrix</p>
            <h2 id="feature-table-title">Business Software Feature Comparison Table</h2>
            <p>
              This table describes typical software category purposes. Actual features
              depend on the specific product and configuration.
            </p>
          </div>
          <ComparisonTable rows={featureMatrix} columns={["Feature", "ERP", "Billing", "Accounting", "Inventory", "POS"]} label="Feature comparison across software categories" />
        </div>
      </section>

      <section className="billing-section cmp-guide" aria-labelledby="need-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Decision guide</p>
            <h2 id="need-title">Which Business Software Do You Need?</h2>
            <p>
              Start with the task that matters most, then decide whether you need that
              task alone or connected with other business functions.
            </p>
          </div>
          <div className="cmp-decision-grid">
            {decisionGuide.map(([need, answer, link]) => (
              <Link to={link} key={need}>
                <span>{need}</span>
                <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
                <strong>{answer}</strong>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="checklist-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Buying checklist</p>
            <h2 id="checklist-title">Business Software Buying Checklist</h2>
          </div>
          <div className="cmp-check-grid">
            {checklist.map((item) => (
              <div key={item}><FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />{item}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section cmp-questions" aria-labelledby="questions-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Before buying</p>
            <h2 id="questions-title">Questions to Ask Before Choosing Business Software</h2>
          </div>
          <div className="cmp-question-grid">
            {buyingQuestions.map((item) => (
              <article key={item}>
                <FontAwesomeIcon icon={faQuestionCircle} aria-hidden="true" />
                <p>{item}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="research-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Feature-based research</p>
            <h2 id="research-title">How to Evaluate Important Business Software Features</h2>
          </div>
          <div className="cmp-research-stack">
            {featureResearch.map(([name, meaning, check, auditus]) => (
              <article key={name}>
                <h3>{name}</h3>
                <p>{meaning}</p>
                <p><strong>Buyer check:</strong> {check}</p>
                <p><strong>Auditus-iERP:</strong> {auditus}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section cmp-soft" aria-labelledby="industry-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Industry comparison</p>
            <h2 id="industry-title">Choosing Software Based on Your Business Type</h2>
            <p>
              The right software mix depends on the business model. These combinations
              use Auditus-iERP modules already supported across the project.
            </p>
          </div>
          <div className="cmp-industry-grid">
            {industryComparisons.map(([business, modules]) => (
              <article key={business}>
                <FontAwesomeIcon icon={faStore} aria-hidden="true" />
                <h3>{business}</h3>
                <p>{modules}</p>
              </article>
            ))}
          </div>
          <div className="cmp-inline-links">
            <Link to="/industries">Explore industry solutions</Link>
          </div>
        </div>
      </section>

      <section className="billing-section" aria-labelledby="screens-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Real product screenshots</p>
            <h2 id="screens-title">Compare with Real Auditus-iERP Screens</h2>
          </div>
          <div className="cmp-screenshot-grid">
            {screenshots.map(([src, alt, label]) => (
              <figure key={src}>
                <img src={src} alt={alt} loading="lazy" width="640" height="390" />
                <figcaption>{label}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section cmp-fit" aria-labelledby="auditus-fit-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">Where Auditus-iERP fits</p>
            <h2 id="auditus-fit-title">Where Auditus-iERP Fits</h2>
            <p>
              Auditus-iERP is designed as an integrated business management platform
              for teams that want billing, accounts, stock, GST, POS, purchases, sales,
              customers, suppliers, and reports connected in one system.
            </p>
          </div>
          <div className="cmp-module-map">
            {auditusModules.map(([name, link]) => (
              <Link to={link} key={name}>{name}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="billing-section cmp-dark" aria-labelledby="integrated-benefits-title">
        <div className="billing-container billing-two-col">
          <div>
            <p className="billing-kicker">Integrated platform value</p>
            <h2 id="integrated-benefits-title">Why Consider an Integrated Platform?</h2>
          </div>
          <div className="billing-copy">
            <p>
              An integrated platform can help teams keep workflows connected, reduce
              duplicate entry, centralize business information, connect reporting, and
              manage daily operations from a shared set of records.
            </p>
            <p>
              This does not mean every business must use ERP immediately. It means
              businesses should compare their actual workflows before choosing separate
              software or an integrated system.
            </p>
          </div>
        </div>
      </section>

      <section className="billing-section billing-faq-section" aria-labelledby="faq-title">
        <div className="billing-container">
          <div className="billing-heading">
            <p className="billing-kicker">FAQ</p>
            <h2 id="faq-title">Business Software Comparison Questions</h2>
          </div>
          <div className="billing-faq-list">
            {faqs.map(([q, a], index) => {
              const open = openFaq === index;
              return (
                <article className={`billing-faq-item ${open ? "is-open" : ""}`} key={q}>
                  <h3>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-controls={`comparisons-faq-${index}`}
                      onClick={() => setOpenFaq(open ? null : index)}
                    >
                      <span className="billing-faq-symbol" aria-hidden="true">{open ? "-" : "+"}</span>
                      {q}
                    </button>
                  </h3>
                  <div id={`comparisons-faq-${index}`} className="billing-faq-answer" aria-hidden={!open}>
                    <p>{a}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="billing-final">
        <div className="billing-container">
          <div className="billing-final-panel">
            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
            <p className="billing-final-kicker">Research with a real product</p>
            <h2>Still Comparing Your Options?</h2>
            <p>
              Explore Auditus-iERP and see whether its connected ERP, billing,
              accounting, inventory, POS, GST, and reporting workflows fit your business.
            </p>
            <div className="billing-actions">
              <Link to="/features-solutions" className="billing-btn billing-btn-primary">
                Explore Auditus-iERP <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
              </Link>
              <Link to="/Demo" className="billing-btn billing-btn-secondary">
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
